/**
 * Progress engine.
 *
 * A direct port of the weighting logic in the approved project tracker: the
 * dashboard, every report and the API's derived figures all go through here so
 * there is exactly one definition of "how much of the scheme is finished".
 *
 * Rules it encodes:
 *  - Only weeks approved by the PMU count towards physical progress.
 *  - Drains are measured by length completed against total length.
 *  - Unit groups are measured by construction stage, using each stage's
 *    cumulative weight; a unit that clears a stage earns the increment.
 *  - Civil work is the mean of its five components, electrical the mean of its
 *    four, mechanical the mean of its two; the overall figure weights those
 *    three heads 51.6 / 25.5 / 22.9.
 *  - Contractor payments count from the day they are recorded (they come from
 *    the treasury record and need no site verification); weekly expenses count
 *    once their week is approved.
 */
import { BUDGET, WORKS_BUDGET, CATS, MISC_MILESTONES, NEWD, REHAB, SCHEME_END, SCHEME_START, UNITS, componentList, } from './domain.js';
const DAY_MS = 86400000;
/** Coerce anything to a finite number, defaulting to 0 — the tracker's `n()`. */
export function num(v) {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
}
/** Thousands-separated number with a fixed number of decimals (default 0). */
export function fmt(v, d) {
    return num(v).toLocaleString('en-US', {
        minimumFractionDigits: d || 0,
        maximumFractionDigits: d === undefined ? 0 : d,
    });
}
/** One decimal place, always shown. */
export function pct1(v) {
    return (Math.round(v * 10) / 10).toFixed(1);
}
/** "9 April 2026" */
export function dLong(s) {
    if (!s)
        return '—';
    const d = new Date(s + 'T00:00:00');
    if (isNaN(d.getTime()))
        return s;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
/** "09 Apr 26" */
export function dShort(s) {
    if (!s)
        return '—';
    const d = new Date(s + 'T00:00:00');
    if (isNaN(d.getTime()))
        return s;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}
/** Local-time ISO date (never UTC — a week must not slip a day by timezone). */
export function iso(d) {
    const p = (n) => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}
/** Today as an ISO date, in local time. */
export function todayLocalIso() {
    return iso(new Date());
}
/** Add days to an ISO date string, in local time. */
export function isoAddDays(s, days) {
    const d = new Date(s + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return iso(d);
}
/** Net released on a bill: gross less retention and taxes, never below zero. */
export function netOf(p) {
    return Math.max(0, num(p.gross) - num(p.retention) - num(p.tax));
}
/** The document ID the field proforma carries, e.g. `1-3-2304290423-26`. */
export function docIdFor(no, start, end) {
    const p2 = (x) => String(x).padStart(2, '0');
    const sd = new Date(start + 'T00:00:00');
    const ed = new Date(end + 'T00:00:00');
    if (isNaN(sd.getTime()) || isNaN(ed.getTime()))
        return '';
    return ('1-' +
        no +
        '-' +
        p2(sd.getDate()) +
        p2(sd.getMonth() + 1) +
        p2(ed.getDate()) +
        p2(ed.getMonth() + 1) +
        '-' +
        String(ed.getFullYear()).slice(2));
}
/** Chart geometry of the progress curve, in SVG user units. */
export const CHART = { W: 680, H: 210, l: 36, r: 12, t: 12, b: 26 };
/** Circumference of the gauge ring (r = 54). */
export const GAUGE_C = 339.29;
export class Engine {
    data;
    /** "Today" — injectable so reports and tests are deterministic. */
    now;
    constructor(data, now = new Date()) {
        this.data = data;
        this.now = now;
    }
    // ------------------------------------------------------------- weeks
    weeks() {
        return this.data.weeks ?? [];
    }
    sortedWeeks() {
        return this.weeks()
            .slice()
            .sort((a, b) => num(a.no) - num(b.no));
    }
    /** Approved weeks in week-number order — the only weeks that count. */
    appr() {
        return this.sortedWeeks().filter((w) => w.status === 'approved');
    }
    payList() {
        return (this.data.payments ?? [])
            .slice()
            .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    }
    // ---------------------------------------------------------- quantities
    /** Feet of a drain completed across approved weeks, optionally up to an index. */
    cumDrain(kind, key, upto) {
        let s = 0;
        this.appr().forEach((w, i) => {
            if (upto === undefined || i <= upto)
                s += num((w[kind] || {})[key]);
        });
        return s;
    }
    /** Units that cleared a stage across approved weeks, optionally up to an index. */
    cumStage(gk, si, upto) {
        let s = 0;
        this.appr().forEach((w, i) => {
            if (upto === undefined || i <= upto)
                s += num(((w.units || {})[gk] || {})[si]);
        });
        return s;
    }
    /** Percent of a whole drain family completed, by length. */
    drainPct(kind, upto) {
        const list = kind === 'drainsNew' ? NEWD : REHAB;
        let tot = 0;
        let done = 0;
        list.forEach((d) => {
            tot += d.ft;
            done += Math.min(d.ft, this.cumDrain(kind, d.k, upto));
        });
        return tot ? (done / tot) * 100 : 0;
    }
    /** Percent of a unit group completed, by cumulative stage weight. */
    unitPct(g, upto) {
        let acc = 0;
        let prev = 0;
        g.stages.forEach((st, i) => {
            const inc = st[1] - prev;
            prev = st[1];
            acc += Math.min(g.total, this.cumStage(g.k, i, upto)) * inc;
        });
        return g.total ? acc / g.total : 0;
    }
    /** Percent complete of every component, keyed by component key. */
    compPcts(upto) {
        const o = {
            newDrains: this.drainPct('drainsNew', upto),
            rehabDrains: this.drainPct('drainsRehab', upto),
        };
        UNITS.forEach((g) => {
            o[g.k] = this.unitPct(g, upto);
        });
        return o;
    }
    /** Head-wise and overall physical progress. */
    catPcts(upto) {
        const c = this.compPcts(upto);
        const civ = (c.newDrains + c.rehabDrains + c.quarters + c.pumpHouses + c.pumpStations) / 5;
        const ele = (c.solarTW + c.elecTW + c.solarPS + c.elecPS) / 4;
        const mec = (c.mechTW + c.mechPS) / 2;
        return {
            civil: civ,
            elec: ele,
            mech: mec,
            overall: civ * 0.516 + ele * 0.255 + mec * 0.229,
            comp: c,
        };
    }
    // ---------------------------------------------------------- money
    /**
     * Expenditure by head. Weekly expenses count once their week is approved;
     * contractor payments count from their own date. When `upto` is given, the
     * cut-off is the end date of that approved week.
     */
    spentBy(upto) {
        const o = { civil: 0, elec: 0, mech: 0, misc: 0, total: 0 };
        const add = (cat, a) => {
            const k = cat || 'misc';
            o[k] = (o[k] || 0) + a;
            o.total += a;
        };
        const ws = this.appr();
        ws.forEach((w, i) => {
            if (upto !== undefined && i > upto)
                return;
            (w.expenses || []).forEach((e) => add(e.cat, num(e.amt)));
        });
        const cutoff = upto !== undefined && ws[upto] ? ws[upto].end || ws[upto].start : null;
        this.payList().forEach((p) => {
            if (cutoff && String(p.date || '') > cutoff)
                return;
            add(p.cat, netOf(p));
        });
        return o;
    }
    /**
     * Financial progress. `worksPct` counts only the construction heads against
     * the construction budget and is the figure to set beside physical progress;
     * `overallPct` is utilisation of the whole sanctioned cost.
     */
    finPcts(upto) {
        const s = this.spentBy(upto);
        const works = (s.civil || 0) + (s.elec || 0) + (s.mech || 0);
        return {
            works,
            worksPct: (works / WORKS_BUDGET) * 100,
            overallPct: (s.total / BUDGET) * 100,
        };
    }
    /** Percent of the miscellaneous head complete, by milestone weight. */
    miscPct() {
        const m = this.data.misc || {};
        let acc = 0;
        MISC_MILESTONES.forEach((x) => {
            acc += (num(m[x.k]) * x.w) / 100;
        });
        return acc;
    }
    /** Net money released per calendar month, oldest first. */
    expByMonth() {
        const m = {};
        this.appr().forEach((w) => (w.expenses || []).forEach((e) => {
            const k = String(e.date || w.end || '').slice(0, 7);
            if (k)
                m[k] = (m[k] || 0) + num(e.amt);
        }));
        this.payList().forEach((p) => {
            const k = String(p.date || '').slice(0, 7);
            if (k)
                m[k] = (m[k] || 0) + netOf(p);
        });
        return Object.keys(m)
            .sort()
            .map((k) => ({ k, v: m[k] }));
    }
    // ---------------------------------------------------------- time
    timeInfo() {
        const s = new Date(SCHEME_START + 'T00:00:00');
        const e = new Date(SCHEME_END + 'T00:00:00');
        const total = Math.round((e.getTime() - s.getTime()) / DAY_MS);
        const elapsed = Math.max(0, Math.min(total, Math.round((this.now.getTime() - s.getTime()) / DAY_MS)));
        return { total, elapsed, remaining: total - elapsed, pct: total ? (elapsed / total) * 100 : 0 };
    }
    /** Percent of the scheme's calendar consumed by a given date. */
    timeAt(ds) {
        const s = new Date(SCHEME_START + 'T00:00:00');
        const e = new Date(SCHEME_END + 'T00:00:00');
        const d = new Date((ds || SCHEME_START) + 'T00:00:00');
        const tot = e.getTime() - s.getTime();
        if (!tot || isNaN(d.getTime()))
            return 0;
        return Math.max(0, Math.min(100, ((d.getTime() - s.getTime()) / tot) * 100));
    }
    // ---------------------------------------------------------- charts
    /** Work / time / money at the close of every approved week. */
    trendSeries() {
        return this.appr().map((w, i) => ({
            no: w.no,
            end: w.end,
            actual: this.catPcts(i).overall,
            time: this.timeAt(w.end),
            fin: this.finPcts(i).worksPct,
        }));
    }
    xAt(i, n) {
        const w = CHART.W - CHART.l - CHART.r;
        return n <= 1 ? CHART.l + w / 2 : CHART.l + (i * w) / (n - 1);
    }
    yAt(v, max) {
        const h = CHART.H - CHART.t - CHART.b;
        return CHART.H - CHART.b - (Math.max(0, Math.min(max, v)) / max) * h;
    }
    // ---------------------------------------------------------- packages
    /** Mean progress of the components assigned to a package. */
    pkgPct(pkgId, comp) {
        const c = comp ?? this.compPcts();
        const ks = Object.keys(this.data.assign || {}).filter((k) => this.data.assign[k] === pkgId);
        if (!ks.length)
            return 0;
        return ks.reduce((a, k) => a + (c[k] || 0), 0) / ks.length;
    }
    /** Name of the package a component is assigned to. */
    pkgOf(componentKey) {
        const id = (this.data.assign || {})[componentKey];
        const p = (this.data.packages || []).find((x) => x.id === id);
        return p ? p.name || 'Unnamed package' : '—';
    }
    // ---------------------------------------------------------- periods
    /** Months that carry at least one approved week, most recent first. */
    monthKeys() {
        const s = new Set();
        this.appr().forEach((w) => {
            const k = (w.end || w.start || '').slice(0, 7);
            if (k)
                s.add(k);
        });
        return Array.from(s).reverse();
    }
    /** The weeks a weekly or monthly report covers. */
    periodWeeks(report, period) {
        const ws = this.appr();
        if (!ws.length)
            return { list: [], upto: undefined, label: 'No approved weeks yet' };
        if (report === 'month') {
            const key = period || this.monthKeys()[0];
            const list = ws.filter((w) => (w.end || w.start || '').slice(0, 7) === key);
            const lastIdx = list.length ? ws.indexOf(list[list.length - 1]) : ws.length - 1;
            const d = new Date(key + '-01T00:00:00');
            return {
                list,
                upto: lastIdx,
                first: list.length ? ws.indexOf(list[0]) : 0,
                label: 'Month of ' + d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
            };
        }
        const id = period || ws[ws.length - 1].id;
        const w = ws.find((x) => x.id === id) || ws[ws.length - 1];
        const i = ws.indexOf(w);
        return {
            list: [w],
            upto: i,
            first: i,
            label: 'Week ' + w.no + ' · ' + dLong(w.start) + ' to ' + dLong(w.end),
        };
    }
    // ---------------------------------------------------------- totals
    /** Feet of drain dug or restored, against the scheme total. */
    drainTotals() {
        const done = NEWD.reduce((a, d) => a + Math.min(d.ft, this.cumDrain('drainsNew', d.k)), 0) +
            REHAB.reduce((a, d) => a + Math.min(d.ft, this.cumDrain('drainsRehab', d.k)), 0);
        const total = NEWD.reduce((a, d) => a + d.ft, 0) + REHAB.reduce((a, d) => a + d.ft, 0);
        return { done, total };
    }
    /** Structures and machinery units fully handed over, against the scheme total. */
    structureTotals() {
        return {
            done: UNITS.reduce((a, g) => a + Math.min(g.total, this.cumStage(g.k, g.stages.length - 1)), 0),
            total: UNITS.reduce((a, g) => a + g.total, 0),
        };
    }
    /** Components running behind the elapsed time, worst first is not applied — source order is kept. */
    laggingComponents() {
        const comp = this.compPcts();
        const t = this.timeInfo();
        return componentList()
            .map((c) => ({
            name: c.n,
            pkg: this.pkgOf(c.k),
            pct: comp[c.k],
            gap: t.pct - comp[c.k],
        }))
            .filter((r) => r.pct < t.pct);
    }
    budgetOf(cat) {
        return CATS.find((c) => c.k === cat).budget;
    }
}
// ------------------------------------------------------------ palettes
export function sevColors(s) {
    if (s === 'Critical')
        return { fg: '#a3372c', bg: '#fbeae7' };
    if (s === 'Major')
        return { fg: '#b06a12', bg: '#fdf3e3' };
    return { fg: '#5a6b68', bg: '#eef2f1' };
}
export function statusColors(s) {
    if (s === 'approved')
        return { fg: '#2f7d51', bg: '#eaf5ee' };
    if (s === 'submitted')
        return { fg: '#b06a12', bg: '#fdf3e3' };
    if (s === 'returned')
        return { fg: '#a3372c', bg: '#fbeae7' };
    return { fg: '#5a6b68', bg: '#eef2f1' };
}
/** Completion scale used on every progress bar. */
export function barColor(p) {
    return p >= 66 ? '#2b7d55' : p >= 25 ? '#0f6b5f' : p > 0 ? '#c9a227' : '#c9d5d2';
}
export function gaugeDash(v) {
    return ((Math.min(100, Math.max(0, v)) / 100) * GAUGE_C).toFixed(1) + ' ' + GAUGE_C;
}
//# sourceMappingURL=engine.js.map